import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RichTextEditor } from "@/components/ui/rich-text-editor";
import { FormField } from "@/components/ui/form-field";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Link, MapPin, Euro, Plus, X, Bus, FileText, Route, Library, AlertTriangle } from "lucide-react";
import { TransportationActivity } from "../models/activity.model";
import { UploadMultiFiles } from "@/components/ui/upload-multi-files";
import { DayField } from "../components/DayField";
import { isTemplateAxis, type DayAxis } from "../models/day-axis";
import { useI18n } from "@/hooks/useI18n";
import ActivityModalShell from "./ActivityModalShell";
import ActivityDetailView from "./ActivityDetailView";
import { LibraryPicker } from "../components/LibraryPicker";
import type { LibraryActivity } from "@/lib/library/models/library-activity.model";

type TransportationTab = 'details' | 'route' | 'documents' | 'library';

interface TransportationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (activity: Partial<TransportationActivity>) => void;
  icon?: React.ReactNode;
  initialData?: Partial<TransportationActivity>;
  itineraryId: string;
  itineraryStartDate?: string | null;
  itineraryEndDate?: string | null;
  /** Asse dei giorni: sui template ogni tappa si colloca su "Giorno N". */
  axis?: DayAxis | null;
  defaultDate?: string;
  /** Sola lettura: campi non interagibili, footer con solo "Chiudi" (usato dalla Libreria). */
  readOnly?: boolean;
  /** Il cliente ha gia' pagato: costo e "includi nel prezzo" sono consolidati. */
}

const TransportationModal = ({ isOpen, onClose, onSave, icon, initialData, itineraryId, itineraryStartDate, itineraryEndDate, axis, defaultDate, readOnly = false }: TransportationModalProps) => {
  const { t } = useI18n();

  // Generate temporary UUID for new activities
  const [activityId] = useState<string>(() =>
    initialData?.id || crypto.randomUUID()
  );

  const [formData, setFormData] = useState<Partial<TransportationActivity>>({
    id: activityId,
    include_price: true,
    title: '',
    link: '',
    transport_type: 'train',
    company: '',
    travels: [
      { address: '', date: undefined, time: '' },
      { address: '', date: undefined, time: '' }
    ],
    cost: 0,
    description: '',
    documentation: []
  });

  // Update form data when initialData changes
  useEffect(() => {
    if (initialData) {
      setFormData({ ...initialData, id: activityId });
    }
  }, [initialData, activityId]);

  const [errors, setErrors] = useState<Record<string, boolean>>({});
  // Import da libreria in due tempi: si sceglie, si conferma, poi si torna ai dati.
  const [pendingImport, setPendingImport] = useState<LibraryActivity | null>(null);
  const [showValidationBanner, setShowValidationBanner] = useState(false);
  const [activeTab, setActiveTab] = useState<TransportationTab>('details');

  useEffect(() => {
    if (!isOpen) {
      setShowValidationBanner(false);
      setErrors({});
      setActiveTab('details');
      setPendingImport(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && !initialData && defaultDate) {
      setFormData(prev => ({
        ...prev,
        travels: prev.travels?.map(t => ({ ...t, date: defaultDate })) || prev.travels
      }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const addTravel = (index: number) => {
    setFormData(prev => ({
      ...prev,
      travels: [
        ...(prev.travels || []).slice(0, index + 1),
        { address: '', date: '', time: '' },
        ...(prev.travels || []).slice(index + 1)
      ]
    }));
  };

  const removeTravel = (index: number) => {
    setFormData(prev => ({
      ...prev,
      travels: prev.travels?.filter((_, i) => i !== index) || []
    }));
  };

  const updateTravel = (index: number, field: keyof TransportationActivity['travels'][0], value: string | number) => {
    setFormData(prev => ({
      ...prev,
      travels: prev.travels?.map((travel, i) =>
        i === index ? { ...travel, [field]: value } : travel
      ) || []
    }));
  };

  const validateForm = (): Record<string, boolean> => {
    const requiredFields = ['title', 'company', 'cost'];
    const newErrors: Record<string, boolean> = {};

    requiredFields.forEach(field => {
      const value = formData[field as keyof TransportationActivity];
      if (field === 'cost') {
        newErrors[field] = value === null || value === undefined || (value as number) < 0;
      } else {
        newErrors[field] = !value || (typeof value === 'string' && value.trim() === '');
      }
    });

    if (!formData.travels || formData.travels.length === 0) {
      newErrors.travels = true;
    } else {
      formData.travels.forEach((travel, index) => {
        const travelRequiredFields = ['address', 'time'];
        travelRequiredFields.forEach(field => {
          const value = travel[field as keyof typeof travel];
          if (!value || (typeof value === 'string' && value.trim() === '')) {
            newErrors[`travel_${index}_${field}`] = true;
          }
        });
        // Sui template la tratta ha un giorno relativo, non una data.
        if (isTemplateAxis(axis) ? !travel.dayIndex : !travel.date) {
          newErrors[`travel_${index}_date`] = true;
        }
      });
    }

    setErrors(newErrors);
    return newErrors;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors = validateForm();
    if (!Object.values(newErrors).some(error => error)) {
      onSave({
        ...formData,
        type: 'transportation' as const,
        date: formData.travels ? formData.travels[0].date : undefined
      });
      onClose();
      return;
    }
    setShowValidationBanner(true);
    const detailsFailed = ['title', 'company', 'cost'].some(field => newErrors[field]);
    setActiveTab(detailsFailed ? 'details' : 'route');
  };

  const transportTypeKeys: Record<string, string> = {
    train: 'modals.transportTypes.train',
    bus: 'modals.transportTypes.bus',
    car: 'modals.transportTypes.car',
    taxi: 'modals.transportTypes.taxi',
    metro: 'modals.transportTypes.metro',
    other: 'modals.transportTypes.other',
  };

  const requiredError = String(t('validation.fieldRequired'));

  // In modifica non si importa: il tab compare solo creando una nuova attività.
  const canImportFromLibrary = !initialData;

  const confirmImport = () => {
      if (!pendingImport) return;
      const imported = pendingImport.toItineraryActivity(defaultDate);
      // L'id resta quello di questa modale: l'import precompila un inserimento nuovo,
      // non porta con sé l'identità dell'attività di libreria.
      setFormData({ ...(imported as Partial<TransportationActivity>), id: activityId });
      setPendingImport(null);
      setErrors({});
      setShowValidationBanner(false);
      setActiveTab('details');
  };
  const hasDetailsError = ['title', 'company', 'cost'].some(field => errors[field]);
  // Il percorso vive in un tab suo: i suoi errori non devono accendere "Dati".
  const hasRouteError = Object.keys(errors).some(key => errors[key] && (key === 'travels' || key.startsWith('travel_')));

  if (readOnly) {
    return (
      <ActivityDetailView
        isOpen={isOpen}
        onClose={onClose}
        icon={icon}
        title={t('builder.activities.transportation')}
        activity={formData}
      />
    );
  }

  return (
    <ActivityModalShell
      isOpen={isOpen}
      onClose={onClose}
      icon={icon}
      title={t('builder.activities.transportation')}
      formId="transportation-form"
      onSubmit={handleSubmit}
      showValidationBanner={showValidationBanner}
      activeTab={activeTab}
      onActiveTabChange={(value) => setActiveTab(value as TransportationTab)}
      tabs={[
        {
          key: 'details',
          label: t('modals.tabDetails'),
          icon: <Bus className="h-3.5 w-3.5 shrink-0" />,
          hasError: hasDetailsError,
          content: (
            <div className="space-y-5">
              {/* Prima riga: a sinistra chi e dove, a destra quanto costa. */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-4">
                  <FormField
                    id="title"
                    label={t('modals.transportationName')}
                    required
                    error={errors.title ? requiredError : undefined}
                  >
                    <Input
                      id="title"
                      value={formData.title || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                      placeholder={t('modals.transportationName')}
                      className="h-9"
                      hasError={errors.title}
                    />
                  </FormField>

                  <FormField
                    id="company"
                    label={t('modals.company')}
                    required
                    error={errors.company ? requiredError : undefined}
                  >
                    <Input
                      id="company"
                      value={formData.company || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))}
                      placeholder={t('modals.company')}
                      className="h-9"
                      hasError={errors.company}
                    />
                  </FormField>
                </div>

                <div className="space-y-4">
                  <FormField
                    id="cost"
                    label={t('modals.price')}
                    icon={<Euro className="h-3 w-3" />}
                    required
                    hint={t('modals.costHint')}
                    error={errors.cost ? requiredError : undefined}
                  >
                    <Input
                      id="cost"
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.cost || 0}
                      onChange={(e) => setFormData(prev => ({ ...prev, cost: parseFloat(e.target.value) }))}
                      placeholder="0,00"
                      className="h-9 tabular-nums"
                      hasError={errors.cost}
                    />
                  </FormField>

                <div className="flex items-start justify-between gap-4 rounded-md bg-gray-50 px-3 py-2.5">
                  <div className="space-y-0.5">
                    <p className="text-xs text-gray-700">{t('modals.includePrice')}</p>
                    <p className="text-[11px] text-gray-500">{t('modals.includePriceHint')}</p>
                  </div>
                  <Switch
                    checked={formData.include_price || false}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, include_price: checked }))}
                  />
                </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField label={t('modals.detail.transportType')} required>
                  <Select
                    value={formData.transport_type || 'train'}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, transport_type: value as TransportationActivity['transport_type'] }))}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder={t('modals.selectTransportType')} />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(transportTypeKeys).map(([value, key]) => (
                        <SelectItem key={value} value={value}>
                          {t(key as any)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormField>

              </div>

              <FormField label={t('modals.note')}>
                <RichTextEditor
                  value={formData.description || ''}
                  onChange={(value) => setFormData(prev => ({ ...prev, description: value }))}
                  placeholder={t('modals.shareClientPreferencesTransport')}
                />
              </FormField>
            </div>
          ),
        },
        {
          key: 'route',
          label: t('modals.travelRoute'),
          icon: <Route className="h-3.5 w-3.5 shrink-0" />,
          hasError: hasRouteError,
          content: (
              <div className="space-y-2">
                {/* Una tappa per riga: partenza, eventuali soste, arrivo. */}
                {formData.travels?.map((travel, index) => (
                  <div
                    key={index}
                    className="flex flex-wrap items-center gap-2 rounded-md border border-input p-2"
                  >
                    <span className="w-14 shrink-0 text-[11px] text-gray-500">
                      {index === 0
                        ? t('common.from')
                        : index === formData.travels!.length - 1
                          ? t('common.to')
                          : t('modals.stopLabel')}
                    </span>
                    <div className="relative min-w-[10rem] flex-1">
                      <MapPin className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-500" />
                      <Input
                        value={travel.address}
                        onChange={(e) => updateTravel(index, 'address', e.target.value)}
                        placeholder={
                          index === 0
                            ? t('modals.departureLocation')
                            : index === formData.travels!.length - 1
                              ? t('modals.arrivalLocation')
                              : t('modals.stopLocation')
                        }
                        className="h-8 pl-8 text-sm"
                        hasError={errors[`travel_${index}_address`]}
                      />
                    </div>
                    <div className="w-40 shrink-0">
                      <DayField
                        axis={axis}
                        date={travel.date}
                        dayIndex={travel.dayIndex}
                        onDateChange={(date) => updateTravel(index, 'date', date)}
                        onDayIndexChange={(dayIndex) => updateTravel(index, 'dayIndex', dayIndex)}
                        placeholder={t('modals.date')}
                        hasError={errors[`travel_${index}_date`]}
                        minDate={itineraryStartDate || undefined}
                        maxDate={itineraryEndDate || undefined}
                        className="h-8"
                      />
                    </div>
                    <Input
                      type="time"
                      value={travel.time}
                      onChange={(e) => updateTravel(index, 'time', e.target.value)}
                      className="h-8 w-28 shrink-0 text-sm"
                      hasError={errors[`travel_${index}_time`]}
                    />
                    {formData.travels && formData.travels.length > 2 && index !== 0 && index !== formData.travels.length - 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeTravel(index)}
                        className="h-8 w-8 shrink-0 p-0 text-gray-500"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}

                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => addTravel(formData.travels!.length - 2)}
                  className="h-8 w-full border-dashed text-xs font-normal text-gray-600"
                >
                  <Plus className="mr-1.5 h-3.5 w-3.5" />
                  {t('modals.addStop')}
                </Button>
              </div>
          ),
        },
        {
          key: 'documents',
          optional: true,
          label: t('modals.tabDocumentsAndLinks'),
          icon: <FileText className="h-3.5 w-3.5 shrink-0" />,
          hasContent: (formData.documentation?.length || 0) > 0 || !!formData.link,
          content: (
            <div className="space-y-5">
              <FormField label={t('modals.documentation')} hint={t('modals.documentationHint')}>
                <UploadMultiFiles
                  files={formData.documentation || []}
                  onFilesChange={(files) => setFormData(prev => ({ ...prev, documentation: files }))}
                  label={t('modals.uploadBoardingPass')}
                  maxFiles={5}
                  itineraryId={itineraryId}
                  activityId={activityId}
                />
              </FormField>

              <FormField
                id="link"
                label={t('modals.link')}
                icon={<Link className="h-3 w-3" />}
                hint={t('modals.addSupportingLink')}
              >
                <Input
                  id="link"
                  type="url"
                  value={formData.link || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, link: e.target.value }))}
                  placeholder="https://..."
                  className="h-9"
                />
              </FormField>
            </div>
          ),
        },
        {
          key: 'library',
          group: 'import' as const,
          optional: true,
          hidden: !canImportFromLibrary,
          label: t('library.tabImport'),
          icon: <Library className="h-3.5 w-3.5 shrink-0" />,
          content: (
            <div className="space-y-4">
              {pendingImport && (
                <div className="flex flex-col gap-2 rounded-md border border-amber-200 bg-amber-50 p-3">
                  <p className="flex items-center gap-2 text-xs font-medium text-amber-800">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                    {t('library.importConfirmTitle', { title: pendingImport.title })}
                  </p>
                  <p className="text-[11px] text-amber-700">{t('library.importConfirmBody')}</p>
                  <div className="flex gap-2">
                    <Button type="button" size="sm" onClick={confirmImport}>
                      {t('library.importConfirmAction')}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setPendingImport(null)}
                    >
                      {t('common.cancel')}
                    </Button>
                  </div>
                </div>
              )}

              <LibraryPicker
                activityType="transportation"
                enabled={isOpen && activeTab === 'library'}
                onSelect={setPendingImport}
                listClassName="max-h-[40vh]"
              />
            </div>
          ),
        },
      ]}
    />
  );
};

export default TransportationModal;
